import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDocumentVersions } from '@/hooks/useKnowledge';
import { VersionHistory } from '@/components/knowledge/VersionHistory';
import { Button } from '@/components/ui/button';
import { ArrowLeft, GitCompare } from 'lucide-react';

export function KnowledgeVersionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);

  const handleBack = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(`/knowledge/${id}`);
    }
  };

  const { data: versions, isLoading } = useDocumentVersions(id!);

  const handleToggleVersion = (versionId: string) => {
    setSelectedVersions(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      } else {
        // Limit to 2 selections
        if (prev.length >= 2) {
          return [prev[1], versionId];
        }
        return [...prev, versionId];
      }
    });
  };

  const handleCompare = () => {
    if (selectedVersions.length === 2) {
      navigate(`/knowledge/${id}/versions/compare?v1=${selectedVersions[0]}&v2=${selectedVersions[1]}`);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 sm:py-8 space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1 min-w-0">
          <Button variant="ghost" onClick={handleBack} className="shrink-0">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold break-words">Histórico de Versões</h1>
        </div>
        <Button
          onClick={handleCompare}
          disabled={selectedVersions.length !== 2}
          className="w-full sm:w-auto sm:ml-auto"
          title={
            selectedVersions.length === 0
              ? 'Selecione 2 versões na tabela para comparar'
              : selectedVersions.length === 1
                ? 'Selecione mais 1 versão para comparar'
                : 'Comparar as 2 versões selecionadas'
          }
        >
          <GitCompare className="h-4 w-4 mr-2" />
          Comparar Versões
          {selectedVersions.length > 0 && selectedVersions.length < 2 && (
            <span className="ml-1 text-xs opacity-70">({selectedVersions.length}/2)</span>
          )}
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">
          Carregando histórico...
        </div>
      ) : versions && versions.length > 0 ? (
        <VersionHistory
          versions={versions}
          documentId={id!}
          selectedVersions={selectedVersions}
          onToggleVersion={handleToggleVersion}
        />
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          Nenhuma versão encontrada
        </div>
      )}
    </div>
  );
}
